// src/pages/system/SunatSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Chip,
  Paper,
  MenuItem,
  InputAdornment,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  InfoOutlined as InfoIcon,
  Send as SendIcon,
  DeleteSweep as ClearIcon
} from '@mui/icons-material';

const SunatSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Selector de Proveedor: 'lucode' | 'factiliza' | 'nubefact' | 'disabled'
  const [provider, setProvider] = useState('lucode');

  // Estados LuCode / APISunat
  const [lucodeEnabled, setLucodeEnabled] = useState(true);
  const [lucodeEnvironment, setLucodeEnvironment] = useState('sandbox');
  const [lucodeToken, setLucodeToken] = useState('');
  const [lucodeSeriesBoleta, setLucodeSeriesBoleta] = useState('B001');
  const [lucodeSeriesFactura, setLucodeSeriesFactura] = useState('F001');
  const [lucodeAutoEmit, setLucodeAutoEmit] = useState(true);

  // Estados Factiliza
  const [factilizaEnabled, setFactilizaEnabled] = useState(false);
  const [factilizaToken, setFactilizaToken] = useState('');
  const [factilizaApiUrl, setFactilizaApiUrl] = useState('https://api.factiliza.com/v1');
  const [factilizaSeriesBoleta, setFactilizaSeriesBoleta] = useState('B001');
  const [factilizaSeriesFactura, setFactilizaSeriesFactura] = useState('F001');
  const [factilizaAutoEmit, setFactilizaAutoEmit] = useState(true);

  // Estados NubeFact
  const [nubefactEnabled, setNubefactEnabled] = useState(false);
  const [nubefactMode, setNubefactMode] = useState('demo');
  const [nubefactApiUrl, setNubefactApiUrl] = useState('');
  const [nubefactToken, setNubefactToken] = useState('');
  const [nubefactSeriesBoleta, setNubefactSeriesBoleta] = useState('B001');
  const [nubefactAutoEmit, setNubefactAutoEmit] = useState(true);

  const [showToken, setShowToken] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [testResult, setTestResult] = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleClearTestInvoices = async () => {
    setClearing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/finance/sunat/clear-test-invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSnackbar({ open: true, message: data.message || 'Se limpiaron los comprobantes de prueba SUNAT', severity: 'success' });
      setConfirmClearOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Error al limpiar comprobantes SUNAT', severity: 'error' });
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // 1. Cargar LuCode / APISunat
      const resLucode = await fetch('/api/finance/lucode/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resLucode.ok) {
        const dataL = await resLucode.json();
        if (dataL.provider === 'lucode') setProvider('lucode');
        setLucodeEnabled(dataL.enabled ?? true);
        setLucodeEnvironment(dataL.environment || 'sandbox');
        setLucodeToken(dataL.token || '');
        setLucodeSeriesBoleta(dataL.seriesBoleta || 'B001');
        setLucodeSeriesFactura(dataL.seriesFactura || 'F001');
        setLucodeAutoEmit(dataL.autoEmit ?? true);
      }

      // 2. Cargar Factiliza
      const resFactiliza = await fetch('/api/finance/factiliza/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resFactiliza.ok) {
        const dataF = await resFactiliza.json();
        if (dataF.provider === 'factiliza') setProvider('factiliza');
        setFactilizaEnabled(dataF.enabled ?? false);
        setFactilizaToken(dataF.token || '');
        setFactilizaApiUrl(dataF.apiUrl || 'https://api.factiliza.com/v1');
        setFactilizaSeriesBoleta(dataF.seriesBoleta || 'B001');
        setFactilizaSeriesFactura(dataF.seriesFactura || 'F001');
        setFactilizaAutoEmit(dataF.autoEmit ?? true);
      }

      // 3. Cargar NubeFact
      const resNube = await fetch('/api/finance/nubefact/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resNube.ok) {
        const dataN = await resNube.json();
        if (dataN.provider === 'nubefact') setProvider('nubefact');
        setNubefactEnabled(dataN.enabled ?? false);
        setNubefactMode(dataN.mode || 'demo');
        setNubefactApiUrl(dataN.apiUrl || '');
        setNubefactToken(dataN.token || '');
        setNubefactSeriesBoleta(dataN.seriesBoleta || 'B001');
        setNubefactAutoEmit(dataN.autoEmit ?? true);
      }
    } catch (error) {
      console.error('Error fetching SUNAT settings:', error);
      setSnackbar({ open: true, message: 'Error al cargar configuraciones de facturación SUNAT', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setTestResult(null);

    try {
      if (provider === 'lucode') {
        const response = await fetch('/api/finance/lucode/settings', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider: 'lucode',
            enabled: lucodeEnabled,
            environment: lucodeEnvironment,
            token: lucodeToken,
            seriesBoleta: lucodeSeriesBoleta,
            seriesFactura: lucodeSeriesFactura,
            autoEmit: lucodeAutoEmit,
            taxRegime: 'NRUS'
          })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || 'Error al guardar configuración de LuCode / APISunat');
        }
      } else if (provider === 'factiliza') {
        const response = await fetch('/api/finance/factiliza/settings', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider: 'factiliza',
            enabled: factilizaEnabled,
            token: factilizaToken,
            apiUrl: factilizaApiUrl,
            seriesBoleta: factilizaSeriesBoleta,
            seriesFactura: factilizaSeriesFactura,
            autoEmit: factilizaAutoEmit,
            taxRegime: 'NRUS'
          })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || 'Error al guardar configuración de Factiliza');
        }
      } else if (provider === 'nubefact') {
        const response = await fetch('/api/finance/nubefact/settings', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider: 'nubefact',
            enabled: nubefactEnabled,
            mode: nubefactMode,
            apiUrl: nubefactApiUrl,
            token: nubefactToken,
            seriesBoleta: nubefactSeriesBoleta,
            autoEmit: nubefactAutoEmit,
            taxRegime: 'NRUS'
          })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || 'Error al guardar configuración de NubeFact');
        }
      } else if (provider === 'disabled') {
        await fetch('/api/finance/lucode/settings', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider: 'disabled',
            enabled: false,
            environment: lucodeEnvironment,
            token: lucodeToken,
            seriesBoleta: lucodeSeriesBoleta,
            seriesFactura: lucodeSeriesFactura,
            autoEmit: false
          })
        });
      }

      setSnackbar({ open: true, message: '¡Configuración de Facturación SUNAT guardada con éxito!', severity: 'success' });
    } catch (error) {
      console.error('Error saving SUNAT settings:', error);
      setSnackbar({ open: true, message: error.message || 'Error al guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      if (provider === 'lucode') {
        if (!lucodeToken) {
          setSnackbar({ open: true, message: 'Ingrese el API Token de LuCode / APISunat antes de probar', severity: 'warning' });
          setTesting(false);
          return;
        }

        const response = await fetch('/api/finance/lucode/test-connection', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token: lucodeToken, environment: lucodeEnvironment })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Error de conexión');

        setTestResult({ success: true, message: data.message || '¡Conexión establecida correctamente con LuCode / APISunat API v3!' });
        setSnackbar({ open: true, message: '¡Conexión con LuCode / APISunat exitosa!', severity: 'success' });
      } else if (provider === 'factiliza') {
        if (!factilizaToken) {
          setSnackbar({ open: true, message: 'Ingrese el Token de Factiliza API antes de probar la conexión', severity: 'warning' });
          setTesting(false);
          return;
        }

        const response = await fetch('/api/finance/factiliza/test-connection', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ apiUrl: factilizaApiUrl, token: factilizaToken })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Error de conexión');

        setTestResult({ success: true, message: data.message || '¡Conexión establecida correctamente con Factiliza API!' });
        setSnackbar({ open: true, message: '¡Conexión con Factiliza API exitosa!', severity: 'success' });
      } else {
        if (!nubefactApiUrl || !nubefactToken) {
          setSnackbar({ open: true, message: 'Ingrese la Ruta API y Token de NubeFact antes de probar', severity: 'warning' });
          setTesting(false);
          return;
        }

        const response = await fetch('/api/finance/nubefact/test-connection', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ apiUrl: nubefactApiUrl, token: nubefactToken })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Error de conexión');

        setTestResult({ success: true, message: data.message || '¡Conexión establecida correctamente con NubeFact!' });
        setSnackbar({ open: true, message: '¡Conexión con NubeFact exitosa!', severity: 'success' });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({ success: false, message: error.message || 'Error de autenticación o token no válido' });
      setSnackbar({ open: true, message: 'Falla al conectar: ' + error.message, severity: 'error' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar sx={{ bgcolor: '#38bdf8', width: 56, height: 56 }}>
              <ReceiptIcon sx={{ fontSize: 32, color: '#0f172a' }} />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              Facturación Electrónica SUNAT (Boletas / Facturas)
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Emisión de comprobantes electrónicos legales a SUNAT e integración automatizada con LuCode / APISunat, Factiliza o NubeFact
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Configuración del Proveedor
                </Typography>
                <Chip
                  label={provider === 'lucode' ? '⚡ LuCode / APISunat Activo' : provider === 'factiliza' ? '🚀 Factiliza API Activo' : provider === 'nubefact' ? '☁️ NubeFact Activo' : 'Desactivado'}
                  color={provider !== 'disabled' ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Proveedor de Facturación Electrónica SUNAT"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    helperText="Elija el proveedor que utilizará el sistema YinoWISP para emitir comprobantes"
                  >
                    <MenuItem value="lucode">⚡ LuCode / APISunat (API REST v3 - https://docs.apisunat.pe)</MenuItem>
                    <MenuItem value="factiliza">🚀 Factiliza API (Facturación SUNAT + Consulta DNI/RUC)</MenuItem>
                    <MenuItem value="nubefact">☁️ NubeFact (PSE / OSE)</MenuItem>
                    <MenuItem value="disabled">🚫 Desactivado / Emisión Manual</MenuItem>
                  </TextField>
                </Grid>

                {/* Formulario LuCode / APISunat */}
                {provider === 'lucode' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={lucodeEnabled}
                            onChange={(e) => setLucodeEnabled(e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Activar Servicio LuCode / APISunat</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Permite a YinoWISP emitir comprobantes a través de APISunat v3
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={lucodeAutoEmit}
                            onChange={(e) => setLucodeAutoEmit(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Emisión Automática al Cobrar</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Envía la boleta a SUNAT en tiempo real al registrar el pago del cliente
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Entorno de Trabajo (APISunat)"
                        value={lucodeEnvironment}
                        onChange={(e) => setLucodeEnvironment(e.target.value)}
                        helperText="Seleccione Entorno de Pruebas (Sandbox) para validar antes de pasar a Producción"
                      >
                        <MenuItem value="sandbox">🧪 Entorno de Pruebas (Sandbox - https://sandbox.apisunat.pe)</MenuItem>
                        <MenuItem value="production">🚀 Entorno de Producción SUNAT (https://app.apisunat.pe)</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serie de Boleta Electrónica"
                        value={lucodeSeriesBoleta}
                        onChange={(e) => setLucodeSeriesBoleta(e.target.value.toUpperCase())}
                        placeholder="B001"
                        helperText="Serie por defecto para Boletas de Venta (ejemplo: B001)"
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serie de Factura Electrónica"
                        value={lucodeSeriesFactura}
                        onChange={(e) => setLucodeSeriesFactura(e.target.value.toUpperCase())}
                        placeholder="F001"
                        helperText="Serie por defecto para Facturas con RUC (ejemplo: F001)"
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type={showToken ? 'text' : 'password'}
                        label="API Token Secreto de LuCode / APISunat"
                        value={lucodeToken}
                        onChange={(e) => setLucodeToken(e.target.value)}
                        placeholder="Token personal o de empresa copiado desde app.apisunat.pe"
                        helperText="Obtén tu API Token registrándote en el panel de APISunat (https://app.apisunat.pe)"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                                {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Formulario Factiliza */}
                {provider === 'factiliza' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={factilizaEnabled}
                            onChange={(e) => setFactilizaEnabled(e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Activar Servicio Factiliza</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Habilita la facturación y la búsqueda automática de DNI/RUC
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={factilizaAutoEmit}
                            onChange={(e) => setFactilizaAutoEmit(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Emisión Automática al Cobrar</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Envía la boleta a SUNAT en tiempo real al registrar el pago del cliente
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serie de Boleta Electrónica"
                        value={factilizaSeriesBoleta}
                        onChange={(e) => setFactilizaSeriesBoleta(e.target.value.toUpperCase())}
                        placeholder="B001"
                        helperText="Serie de 4 caracteres para Boletas de Venta (ejemplo: B001)"
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serie de Factura Electrónica"
                        value={factilizaSeriesFactura}
                        onChange={(e) => setFactilizaSeriesFactura(e.target.value.toUpperCase())}
                        placeholder="F001"
                        helperText="Serie de 4 caracteres para Facturas con RUC (ejemplo: F001)"
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="URL de API Factiliza"
                        value={factilizaApiUrl}
                        onChange={(e) => setFactilizaApiUrl(e.target.value)}
                        placeholder="https://api.factiliza.com/v1"
                        helperText="URL base del servicio de la API de Factiliza"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type={showToken ? 'text' : 'password'}
                        label="API Token Secreto de Factiliza"
                        value={factilizaToken}
                        onChange={(e) => setFactilizaToken(e.target.value)}
                        placeholder="Token comercial o de pruebas otorgado por Factiliza"
                        helperText="Obtén tu API Token registrándote en Factiliza (https://factiliza.com)"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                                {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Formulario NubeFact */}
                {provider === 'nubefact' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={nubefactEnabled}
                            onChange={(e) => setNubefactEnabled(e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Activar Emisión NubeFact</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Permite a YinoWISP emitir comprobantes a través de NubeFact
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={nubefactAutoEmit}
                            onChange={(e) => setNubefactAutoEmit(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Emisión Automática al Cobrar</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Genera y envía la boleta a SUNAT al confirmar el pago
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Modo de Operación NubeFact"
                        value={nubefactMode}
                        onChange={(e) => setNubefactMode(e.target.value)}
                      >
                        <MenuItem value="demo">Demo / Pruebas</MenuItem>
                        <MenuItem value="production">Producción (Modo EN VIVO - SUNAT)</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serie para Boletas"
                        value={nubefactSeriesBoleta}
                        onChange={(e) => setNubefactSeriesBoleta(e.target.value.toUpperCase())}
                        placeholder="B001"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Ruta de la API NubeFact"
                        value={nubefactApiUrl}
                        onChange={(e) => setNubefactApiUrl(e.target.value)}
                        placeholder="https://api.nubefact.com/api/v1/..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type={showToken ? 'text' : 'password'}
                        label="Token Secreto NubeFact"
                        value={nubefactToken}
                        onChange={(e) => setNubefactToken(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                                {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {testResult && (
                <Box mt={3}>
                  <Alert severity={testResult.success ? 'success' : 'error'} icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}>
                    {testResult.message}
                  </Alert>
                </Box>
              )}

              <Box display="flex" justifyContent="space-between" mt={4} pt={2} borderTop="1px solid #e2e8f0">
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handleTestConnection}
                  disabled={testing || provider === 'disabled'}
                >
                  {testing ? 'Verificando API...' : 'Probar Conexión'}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
            <CardContent p={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <InfoIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Información y Ayuda
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>⚡ LuCode / APISunat API v3</strong>: Permite emitir Boletas y Facturas electrónicas en formato estándar SUNAT utilizando la API REST v3 (`docs.apisunat.pe`).
              </Alert>

              <Typography variant="body2" paragraph color="textSecondary">
                Al activar la <strong>Emisión Automática al Cobrar</strong>, cada vez que registres un pago (manual, Yape, Plin o Pasarela Culqi), YinoWISP enviará automáticamente la Boleta o Factura a SUNAT y almacenará los archivos PDF y XML en el sistema.
              </Typography>

              <Typography variant="body2" paragraph color="textSecondary">
                Puedes cambiar entre el entorno de <strong>Pruebas (Sandbox)</strong> y <strong>Producción SUNAT</strong> en cualquier momento.
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#fff', mt: 3, border: '1px solid #fee2e2' }}>
            <CardContent p={3}>
              <Box display="flex" alignItems="center" mb={1.5}>
                <ClearIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="error">
                  Mantenimiento de Pruebas
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" paragraph>
                Si realizaste emisiones de comprobantes en el entorno de pruebas (Sandbox) y deseas limpiarlos para restaurarlos como recibos internos simples:
              </Typography>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={() => setConfirmClearOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Limpiar Comprobantes de Prueba SUNAT
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          ¿Limpiar Comprobantes de Prueba?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Esta acción desvinculará los folios y comprobantes de prueba de todas las facturas en el sistema, restaurándolas a recibos internos. ¿Deseas continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmClearOpen(false)} disabled={clearing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleClearTestInvoices}
            disabled={clearing}
            startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : <ClearIcon />}
          >
            {clearing ? 'Limpiando...' : 'Sí, Limpiar Registros'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SunatSettings;
